import { FormlyFieldConfig } from '@ngx-formly/core';
import { TripettoNode } from '../utils/tripetto-interfaces';
import { convertProps } from './props-converter';
import { convertAddressBlock } from './address-block-converter';
import { convertLanguagesBlock } from './languages-block-converter';
import { convertStatementBlock } from './statement-block-converter';
import { convertHideExpression } from '../utils/expression-converter';

/**
 * Convert a single Tripetto node to a Formly field configuration
 * @param node The Tripetto node to convert
 * @param blockTypeMapping Mapping of Tripetto block types to Formly field types
 * @param slotLookup Map of slot IDs to field aliases for interpolation
 * @returns FormlyFieldConfig or null if conversion fails
 */
export function convertNode(
	node: TripettoNode,
	blockTypeMapping: Record<string, string>,
	slotLookup?: Map<string, { alias?: string; nodeName?: string; slotId?: string }>
): FormlyFieldConfig | null {
	const blockType = node.block?.type;

	// Handle nodes without a block (pure statement/display nodes in Tripetto)
	if (!blockType) {
		// These are statement nodes - convert to mat-formly-static
		return convertStatementBlock(node, 'mat-formly-static', slotLookup);
	}

	// Map Tripetto block type to Formly field type
	const formlyType = blockTypeMapping[blockType];

	if (!formlyType) {
		return null; // Unsupported block type
	}

	// Handle special case: address block (composite field with multiple slots)
	if (blockType === 'tripetto-block-address') {
		return convertAddressBlock(node);
	}

	// Extract slot data (alias, label, etc.)
	const slot = node.slots?.[0];
	const key = slot?.alias || slot?.id || node.id;

	// Handle special case: statement blocks (display-only)
	if (blockType === 'tripetto-block-statement') {
		return convertStatementBlock(node, formlyType, slotLookup);
	}

	// Handle special case: languages block (i18n display-only)
	if (blockType === 'languages') {
		return convertLanguagesBlock(node, formlyType);
	}

	// Build the Formly field configuration
	const field: FormlyFieldConfig = {
		key,
		type: formlyType,
		props: convertProps(node, slot, slotLookup)
	};

	// Add custom validation messages for email blocks
	if (blockType === 'tripetto-block-email') {
		field.validation = {
			messages: {
				pattern: (error: any, field: FormlyFieldConfig) => {
					const label = field.props?.label || 'Email';
					return `${label} has the wrong email format`;
				}
			}
		};
	}

	// Add custom validation messages for phone number blocks
	if (blockType === 'tripetto-block-phone-number') {
		field.validation = {
			messages: {
				pattern: (error: any, field: FormlyFieldConfig) => {
					const label = field.props?.label || 'Phone Number';
					return `${label} has the wrong phone number format`;
				}
			}
		};
	}

	// Add custom validation messages for URL blocks
	if (blockType === 'tripetto-block-url') {
		field.validation = {
			messages: {
				pattern: (error: any, field: FormlyFieldConfig) => {
					const label = field.props?.label || 'URL';
					return `${label} must be a valid URL (e.g., https://example.com)`;
				}
			}
		};
	}

	// Add expressions.hide if there are simple conditions
	// (Complex conditions are flagged as warnings)
	const hideExpression = convertHideExpression(node);
	if (hideExpression) {
		field.expressions = { hide: hideExpression };
	}

	return field;
}
